<template>
  <form
    @submit.prevent="onSubmit"
    class="fechamentos-form"
    data-testid="fechamentos-form"
  >
    <!-- Alerts -->
    <inline-message v-if="error" severity="error" :closable="true" @close="error = null" data-testid="fechamentos-form-error">
      {{ error }}
    </inline-message>

    <!-- Guidance for creation -->
    <Message v-if="mode === 'create' && !fechamento" severity="info" :closable="false" class="mb-3">
      {{ t('Crie um fechamento para o período mensal. Ele usará o Plano de Contas ativo para comparar valores previstos com o realizado.') }}
    </Message>

    <!-- Franquia -->
    <div class="form-row" data-testid="fechamentos-form-franquia-row">
      <div class="form-col">
        <franchise-select
          v-model="form.franquia_id"
          elem-id="fechamento_franquia_id"
          :label="t('Franquia')"
          :placeholder="t('Selecione a franquia')"
          :disabled="mode === 'edit'"
          data-testid="fechamentos-form-franquia"
        />
      </div>
    </div>

    <!-- Period -->
    <div class="form-row" data-testid="fechamentos-form-periodo-row">
      <div class="form-col">
        <input-date
          v-model="form.data_inicial"
          elem-id="fechamento_data_inicial"
          :label="t('Data inicial')"
          :placeholder="t('Data inicial')"
          :validation="() => isDataInicialValid"
          :required="true"
          :disabled="mode === 'edit'"
          data-testid="fechamentos-form-data-inicial"
        />
      </div>
      <div class="form-col">
        <input-date
          v-model="form.data_final"
          elem-id="fechamento_data_final"
          :label="t('Data final')"
          :placeholder="t('Data final')"
          :validation="() => isDataFinalValid"
          :required="true"
          :disabled="mode === 'edit'"
          data-testid="fechamentos-form-data-final"
        />
      </div>
    </div>
    <small v-if="errors.periodo" class="field-error" data-testid="fechamentos-form-periodo-error">
      {{ errors.periodo }}
    </small>

    <!-- Metas -->
    <div class="form-row" data-testid="fechamentos-form-metas-row">
      <div class="form-col">
        <input-currency
          v-model="form.meta_faturamento"
          elem-id="fechamento_meta_faturamento"
          :label="t('Meta de faturamento')"
          :placeholder="t('Informe a meta de faturamento')"
          :validation="() => isMetaFaturamentoValid"
          :required="true"
          data-testid="fechamentos-form-meta-faturamento"
        />
      </div>
      <div class="form-col">
        <input-number
          v-model="form.meta_clientes"
          elem-id="fechamento_meta_clientes"
          :label="t('Meta de clientes')"
          :placeholder="t('Informe a meta de clientes')"
          :validation="() => isMetaClientesValid"
          :required="true"
          :min="1"
          data-testid="fechamentos-form-meta-clientes"
        />
      </div>
    </div>

    <!-- Meta de itens (opcional) -->
    <div class="form-row" data-testid="fechamentos-form-meta-itens-row">
      <div class="form-col">
        <input-number
          v-model="form.meta_itens"
          elem-id="fechamento_meta_itens"
          :label="t('Meta de itens')"
          :placeholder="t('Informe a meta de itens')"
          :min="1"
          data-testid="fechamentos-form-meta-itens"
        />
      </div>
    </div>

    <!-- Meta valor por cliente (auto-calculated) -->
    <div class="form-row" v-if="metaValorPorCliente !== null" data-testid="fechamentos-form-meta-valor-row">
      <div class="form-col">
        <label class="form-label">{{ t('Meta valor por cliente') }}</label>
        <div class="readonly-value" data-testid="fechamentos-form-meta-valor-por-cliente">
          {{ n(metaValorPorCliente, 'currency') }}
        </div>
        <small class="field-hint">{{ t('Calculado automaticamente') }}</small>
      </div>
    </div>

    <!-- Warning for existing open period -->
    <Message v-if="hasOpenFechamento" severity="warn" :closable="false" class="mb-3" data-testid="fechamentos-form-open-warning">
      <div>
        <strong>{{ t('Já existe um fechamento aberto para esta franquia') }}</strong>
        <p class="mt-1 mb-0" style="font-size: 0.85rem;">
          {{ t('Feche o período atual antes de criar um novo. Cada franquia pode ter apenas um fechamento aberto por vez.') }}
        </p>
      </div>
    </Message>

  </form>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFechamentos } from '../../composables/useFechamentos';
import { log } from '../../utils/DebugLogger';

type FormMode = 'create' | 'edit';

interface FechamentoData {
  id?: number | string;
  franquia_id?: number | string | null;
  data_inicial?: string | null;
  data_final?: string | null;
  meta_faturamento?: number | null;
  meta_clientes?: number | null;
  meta_itens?: number | null;
  [key: string]: unknown;
}

interface FechamentoFormState {
  franquia_id: number | string | null;
  data_inicial: string;
  data_final: string;
  meta_faturamento: number | null;
  meta_clientes: number | null;
  meta_itens: number | null;
}

interface FechamentoFormErrors {
  franquia_id?: string;
  data_inicial?: string;
  data_final?: string;
  periodo?: string;
  meta_faturamento?: string;
  meta_clientes?: string;
}

const { t, n } = useI18n();
const { createFechamento, updateFechamento, fetchFechamentoAberto } = useFechamentos();

// Props
const props = withDefaults(
  defineProps<{
    fechamento?: FechamentoData | null;
    mode?: FormMode;
  }>(),
  {
    fechamento: null,
    mode: 'create',
  }
);

// Emits
const emit = defineEmits<{
  (e: 'saved', result: unknown): void;
  (e: 'cancel'): void;
}>();

// State
const form = ref<FechamentoFormState>({
  franquia_id: null,
  data_inicial: '',
  data_final: '',
  meta_faturamento: null,
  meta_clientes: null,
  meta_itens: null
});

const errors = ref<FechamentoFormErrors>({});
const error = ref<string | null>(null);
const isSubmitting = ref<boolean>(false);
const hasOpenFechamento = ref<boolean>(false);

// Validation computeds
const isFranquiaValid = computed<boolean>(() => !!form.value.franquia_id);

const isDataInicialValid = computed<boolean>(() => !!form.value.data_inicial);

const isDataFinalValid = computed<boolean>(() => {
  if (!form.value.data_final) return false;
  if (form.value.data_inicial && form.value.data_final) {
    return form.value.data_final >= form.value.data_inicial;
  }
  return true;
});

const isMetaFaturamentoValid = computed<boolean>(() => {
  return form.value.meta_faturamento !== null && form.value.meta_faturamento > 0;
});

const isMetaClientesValid = computed<boolean>(() => {
  return form.value.meta_clientes !== null && form.value.meta_clientes > 0;
});

// Auto-calculated: meta faturamento / meta clientes
const metaValorPorCliente = computed<number | null>(() => {
  const fat = form.value.meta_faturamento;
  const clientes = form.value.meta_clientes;
  if (fat !== null && fat > 0 && clientes !== null && clientes > 0) {
    return fat / clientes;
  }
  return null;
});

// canSave computed for modal to check
const canSave = computed<boolean>(() => {
  return isFranquiaValid.value &&
    isDataInicialValid.value &&
    isDataFinalValid.value &&
    isMetaFaturamentoValid.value &&
    isMetaClientesValid.value &&
    !isSubmitting.value;
});

// Auto-fill data_final with last day of month when data_inicial changes
watch(() => form.value.data_inicial, (newDate) => {
  if (newDate && !form.value.data_final) {
    const d = new Date(newDate + 'T00:00:00');
    const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    const mm = String(lastDay.getMonth() + 1).padStart(2, '0');
    const dd = String(lastDay.getDate()).padStart(2, '0');
    form.value.data_final = `${lastDay.getFullYear()}-${mm}-${dd}`;
    log('FechamentosForm', 'watch:data_inicial', 'Auto-filled data_final', form.value.data_final);
  }
});

// Watch franquia_id to check for open fechamento
watch(() => form.value.franquia_id, async (newId) => {
  log('FechamentosForm', 'watch:franquia_id', 'Franchise changed', newId);
  if (newId && props.mode === 'create') {
    try {
      const aberto = await fetchFechamentoAberto(Number(newId));
      hasOpenFechamento.value = !!aberto;
      log('FechamentosForm', 'watch:franquia_id', 'Has open fechamento', hasOpenFechamento.value);
    } catch (e) {
      log('FechamentosForm', 'watch:franquia_id', 'Error checking open fechamento', e);
      hasOpenFechamento.value = false;
    }
  }
});

// Initialize form from fechamento prop
watch(() => props.fechamento, (fechamento) => {
  if (fechamento) {
    log('FechamentosForm', 'watch:fechamento', 'Loading fechamento into form', fechamento);
    loadForEdit(fechamento);
  }
}, { immediate: true });

// Methods
const validate = (): boolean => {
  log('FechamentosForm', 'validate', 'Validating form', form.value);
  errors.value = {};

  if (!form.value.franquia_id) {
    errors.value.franquia_id = t('Franquia e obrigatoria');
  }

  if (!form.value.data_inicial) {
    errors.value.data_inicial = t('Data inicial e obrigatoria');
  }

  if (!form.value.data_final) {
    errors.value.data_final = t('Data final e obrigatoria');
  }

  if (form.value.data_inicial && form.value.data_final) {
    if (form.value.data_final < form.value.data_inicial) {
      errors.value.periodo = t('Data final deve ser posterior a data inicial');
    }
  }

  if (!form.value.meta_faturamento || form.value.meta_faturamento <= 0) {
    errors.value.meta_faturamento = t('Meta de faturamento deve ser maior que zero');
  }

  if (!form.value.meta_clientes || form.value.meta_clientes <= 0) {
    errors.value.meta_clientes = t('Meta de clientes deve ser maior que zero');
  }

  const isValid = Object.keys(errors.value).length === 0;
  log('FechamentosForm', 'validate', 'Validation result', { isValid, errors: errors.value });
  return isValid;
};

const onSubmit = async (): Promise<void> => {
  log('FechamentosForm', 'onSubmit', 'Form submitted', { mode: props.mode });
  if (!validate()) return;

  isSubmitting.value = true;
  error.value = null;

  try {
    const data = {
      franquia_id: Number(form.value.franquia_id),
      data_inicial: form.value.data_inicial,
      data_final: form.value.data_final,
      // validate() guards these are non-null before onSubmit proceeds
      meta_faturamento: form.value.meta_faturamento!,
      meta_clientes: form.value.meta_clientes ?? undefined,
      // meta_itens is optional — omit when the user leaves it blank
      meta_itens: form.value.meta_itens ?? undefined,
    };

    log('FechamentosForm', 'onSubmit', 'Submitting data', data);

    let result: unknown;
    if (props.mode === 'create') {
      result = await createFechamento(data);
    } else {
      result = await updateFechamento(props.fechamento?.id, {
        meta_faturamento: data.meta_faturamento,
        meta_clientes: data.meta_clientes,
        meta_itens: data.meta_itens,
      });
    }

    log('FechamentosForm', 'onSubmit', 'Save successful', result);
    emit('saved', result);
  } catch (err) {
    log('FechamentosForm', 'onSubmit', 'Save error', err);
    const message = err instanceof Error ? err.message : '';
    error.value = message || t('Erro ao salvar fechamento');
  } finally {
    isSubmitting.value = false;
  }
};

/**
 * Save the fechamento (alias for onSubmit, callable by modal via ref)
 */
const saveFechamento = (): Promise<void> => {
  log('FechamentosForm', 'saveFechamento', 'Called by modal ref');
  return onSubmit();
};

/**
 * Load a fechamento into the form for editing
 */
const loadForEdit = (fechamento: FechamentoData): void => {
  log('FechamentosForm', 'loadForEdit', 'Loading for edit', fechamento);
  form.value = {
    franquia_id: fechamento.franquia_id ? String(fechamento.franquia_id) : null,
    data_inicial: fechamento.data_inicial || '',
    data_final: fechamento.data_final || '',
    meta_faturamento:
      fechamento.meta_faturamento !== undefined && fechamento.meta_faturamento !== null
        ? parseFloat(String(fechamento.meta_faturamento))
        : null,
    meta_clientes:
      fechamento.meta_clientes !== undefined && fechamento.meta_clientes !== null
        ? parseFloat(String(fechamento.meta_clientes))
        : null,
    meta_itens:
      fechamento.meta_itens !== undefined && fechamento.meta_itens !== null
        ? parseFloat(String(fechamento.meta_itens))
        : null
  };
};

/**
 * Initialize form for new fechamento creation
 */
const initializeForNew = (): void => {
  log('FechamentosForm', 'initializeForNew', 'Initializing for new fechamento');
  resetForm();
  // franchise-select auto-selects first franchise from filter
};

/**
 * Reset the form to default empty state
 */
const resetForm = (): void => {
  log('FechamentosForm', 'resetForm', 'Resetting form');
  form.value = {
    franquia_id: null,
    data_inicial: '',
    data_final: '',
    meta_faturamento: null,
    meta_clientes: null,
    meta_itens: null
  };
  errors.value = {};
  error.value = null;
  hasOpenFechamento.value = false;
};

// Lifecycle
onMounted(() => {
  log('FechamentosForm', 'onMounted', 'Form mounted', { mode: props.mode, hasFechamento: !!props.fechamento });
});

// Expose methods and computed for modal to use via ref
defineExpose({
  saveFechamento,
  loadForEdit,
  initializeForNew,
  resetForm,
  canSave,
  metaValorPorCliente,
  hasOpenFechamento
});
</script>

<style scoped>
.fechamentos-form {
  padding: 0;
}

/* Flexbox form layout */
.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-col {
  flex: 1 1 calc(50% - 10px);
  min-width: 250px;
}

/* Responsive: Stack columns on tablets and mobile */
@media (max-width: 992px) {
  .form-row {
    flex-direction: column;
  }

  .form-col {
    flex: 1 1 100%;
  }
}

/* Field hints */
.field-hint {
  display: block;
  color: #6c757d;
  font-size: 0.8rem;
  margin-top: 4px;
}

.field-error {
  display: block;
  color: #dc3545;
  font-size: 0.8rem;
  margin-top: 4px;
}

.readonly-value {
  padding: 0.5rem 0.75rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  color: #495057;
  min-height: 38px;
  display: flex;
  align-items: center;
}
</style>
